import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", description: "", points_cost: 100, image_url: "", active: true };

export default function AdminRewards() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/rewards").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (r) => { setEditing(r); setForm(r ? {...r} : EMPTY); setShowForm(true); };
  const close = () => { setEditing(null); setForm(EMPTY); setShowForm(false); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, points_cost: parseInt(form.points_cost) || 0 };
      if (editing && editing.id) {
        await api.put(`/admin/rewards/${editing.id}`, payload);
        toast.success("Récompense mise à jour");
      } else {
        await api.post("/admin/rewards", payload);
        toast.success("Récompense créée");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer "${r.name}" ?`)) return;
    await api.delete(`/admin/rewards/${r.id}`);
    toast.success("Récompense supprimée");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-rewards-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-3">Récompenses</div>
          <h1 className="font-display text-5xl">Catalogue de récompenses</h1>
        </div>
        <button onClick={() => open(null)} className="bg-[#d4af37] text-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a] inline-flex items-center gap-2" data-testid="add-reward-btn">
          <Plus size={16}/> Ajouter
        </button>
      </div>

      <div className="border border-[rgba(253,251,247,0.08)] mt-10 overflow-x-auto fade-up-delay-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgba(253,251,247,0.08)] text-left">
              <th className="px-4 py-3 label-eyebrow">Nom</th>
              <th className="px-4 py-3 label-eyebrow">Coût (pts)</th>
              <th className="px-4 py-3 label-eyebrow">Actif</th>
              <th className="px-4 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#a3a8a4]">Aucune récompense.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-b border-[rgba(253,251,247,0.05)] last:border-0 hover:bg-[#1a241b]/30" data-testid={`admin-reward-${r.id}`}>
                <td className="px-4 py-3">{r.name}</td>
                <td className="px-4 py-3 gold-text">{r.points_cost}</td>
                <td className="px-4 py-3">{r.active ? <span className="gold-text text-xs">Actif</span> : <span className="text-[#a3a8a4] text-xs">Inactif</span>}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => open(r)} className="p-2 border border-[rgba(253,251,247,0.1)] hover:border-[#d4af37]" data-testid={`edit-reward-${r.id}`}><Pencil size={14}/></button>
                    <button onClick={() => remove(r)} className="p-2 border border-[rgba(253,251,247,0.1)] hover:border-red-500 hover:text-red-400" data-testid={`delete-reward-${r.id}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={close} data-testid="reward-form-modal">
          <form onSubmit={save} onClick={(e)=>e.stopPropagation()} className="w-full max-w-lg bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)] p-8 space-y-4 my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <h3 className="font-display text-3xl mb-2">{editing ? "Modifier" : "Ajouter"} une récompense</h3>
            <label className="block">
              <span className="label-eyebrow block mb-2">Nom</span>
              <input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required data-testid="reward-name" className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"/>
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-2">Description</span>
              <textarea value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} rows={3} data-testid="reward-description" className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"/>
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-2">Coût en points</span>
              <input type="number" min="0" value={form.points_cost} onChange={(e)=>setForm({...form, points_cost:e.target.value})} required data-testid="reward-points" className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"/>
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-2">URL de l'image (optionnel)</span>
              <input value={form.image_url} onChange={(e)=>setForm({...form, image_url:e.target.value})} data-testid="reward-image-url" className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"/>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form, active:e.target.checked})} data-testid="reward-active" className="accent-[#d4af37]"/>
              <span className="text-sm">Récompense active</span>
            </label>
            <div className="flex gap-3 pt-4">
              <button type="submit" className="flex-1 bg-[#d4af37] text-black px-5 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a]" data-testid="reward-save">Enregistrer</button>
              <button type="button" onClick={close} className="flex-1 border border-[rgba(253,251,247,0.1)] px-5 py-3 text-xs uppercase tracking-widest hover:border-[#d4af37]" data-testid="reward-cancel">Annuler</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
